import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Share2, RotateCcw, Loader2, Lock, Shield, Sparkles, FileDown, Film, ShieldCheck, RefreshCw, Ban, Globe, Eye, ArrowRight, ChevronDown, BookmarkCheck, ArrowLeft, MessageSquareText, Target, FileText, Camera, Crosshair, Radio } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import html2canvas from "html2canvas";
import ViralPosterCard from "@/components/ViralPosterCard";
import EducationalExplainerCard from "@/components/EducationalExplainerCard";
import PatternFirstCard from "@/components/PatternFirstCard";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";
import { toast } from "@/hooks/use-toast";
import { routeAnalysis } from "@/lib/resultRouter";
import { TACTICAL_DESCRIPTIONS, PATTERN_LABELS } from "@/lib/tacticalDescriptions";
import { useAuth } from "@/contexts/AuthContext";
import SignalChips from "@/components/SignalChips";
import ForensicReportExport from "@/components/ForensicReportExport";
import UnlockStrategyModal from "@/components/UnlockStrategyModal";
import IntelligenceTopBar from "@/components/IntelligenceTopBar";
import NeutralizerChat from "@/components/NeutralizerChat";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import { useOnboarding } from "@/hooks/useOnboarding";
import { exportPdfBrief, exportTextFile, buildTacticalSummary, buildConversationSnapshot } from "@/lib/exportDossier";

type Phase =
  | "scanning"
  | "counting"
  | "impact"
  | "label"
  | "grid"
  | "draw"
  | "dots"
  | "complete";

const DEFAULT_MESSAGE =
  '"I never said that. You\'re remembering it wrong again. Maybe you should talk to someone about your memory issues."';

const VertexDot = ({ cx, cy, visible }: { cx?: number; cy?: number; visible: boolean }) => {
  if (typeof cx !== "number" || typeof cy !== "number") return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill="hsl(184 100% 50%)"
      stroke="hsl(184 100% 70%)"
      strokeWidth={2}
      style={{
        filter: "drop-shadow(0 0 6px hsl(184 100% 50% / 0.45))",
        transform: visible ? "scale(1)" : "scale(0)",
        transformOrigin: `${cx}px ${cy}px`,
        transition: "transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
    />
  );
};

const deriveFallbackSignals = (primaryPattern: string, verdict: string) => {
  switch (primaryPattern) {
    case "neutral_accountability":
      return ["Healthy Boundary", "Conversation Pause"];
    case "passive_aggression":
      return ["Passive Aggression", "Dismissive Ambiguity", "Sarcastic Deflection"];
    case "gaslighting":
      return ["Reality Denial", "Memory Questioning"];
    case "invalidation":
    case "soft_invalidation":
      return ["Emotional Dismissal", "Feeling Minimization"];
    case "guilt_induction":
      return ["Guilt Induction", "Self-Victimization", "Emotional Bait"];
    case "triangulation":
    case "social_pressure":
    case "false_consensus":
      return ["Third-Party Pressure", "Social Leverage", "Consensus Weaponization", "Externalized Blame"];
    case "minimization":
      return ["Minimization Language", "Impact Downplaying"];
    case "ambiguous_responsibility":
      return ["Ambiguous Intent", "Contextual Pressure"];
    case "deflection":
    case "blame_shift":
      return ["Responsibility Deflection", "Narrative Shift"];
    default:
      return verdict.includes("LOW")
        ? ["Healthy Boundary", "Clarification"]
        : ["Mixed Signals", "Context Needed"];
  }
};

const getSimilarPhrases = (pattern: string): string[] => {
  const families: Record<string, string[]> = {
    gaslighting: ["That never happened.", "You're making things up.", "You're remembering it wrong."],
    invalidation: ["You're overreacting.", "You're being dramatic.", "You take everything too personally."],
    soft_invalidation: ["You're being dramatic.", "It's not that serious.", "You take everything too personally."],
    guilt_induction: ["I'm always the bad guy.", "I guess I ruin everything.", "Nothing I do is ever enough for you."],
    passive_aggression: ["Fine, I just won't talk anymore.", "Whatever you say.", "Good to know."],
    triangulation: ["Everyone agrees with me.", "My friends all think you're the problem.", "Ask anyone."],
    false_consensus: ["Everyone agrees with me.", "Nobody else has a problem.", "Ask anyone."],
    minimization: ["It's not that serious.", "It was just a joke.", "You're blowing this out of proportion."],
    deflection: ["That's not what we're talking about.", "You always do this.", "What about when you..."],
    blame_shift: ["You always do this.", "This is your fault.", "You made me do this."],
    neutral_accountability: ["I need space right now.", "Let's talk later.", "I'm not ready to discuss this right now."],
    ambiguous_responsibility: ["I don't remember it that way.", "That's not what happened.", "Maybe we're both misunderstanding."],
    social_pressure: ["Everyone else manages.", "No one else has a problem.", "Other people see it too."],
    control_framing: ["I know what's best.", "You don't understand.", "Trust me on this."],
    victim_reversal: ["I'm always the bad guy.", "You're the one hurting me.", "I can't do anything right."],
  };
  return families[pattern] || ["That's not what I meant.", "You always do this.", "I was just trying to help."];
};

export default function AnalysisResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const onboarding = useOnboarding();
  const passedMessage = (location.state as any)?.message;
  const passedAnalysisId = (location.state as any)?.analysisId;
  const analysisMode = (location.state as any)?.mode || "single";
  const contextMode = ((location.state as any)?.contextMode || "personal") as "professional" | "personal" | "public";
  const relationshipType = (location.state as any)?.relationshipType || null;
  const fromReports = !!(location.state as any)?.fromReports;
  const [loadedMessage, setLoadedMessage] = useState<string | null>(null);
  const [savedResponse, setSavedResponse] = useState<string | null>(null);
  const [savedDecoder, setSavedDecoder] = useState<string | null>(null);
  const [savedSignals, setSavedSignals] = useState<string[] | null>(null);
  const [savedTimestamp, setSavedTimestamp] = useState<string | null>(null);
  const [savedViralScript, setSavedViralScript] = useState<boolean>(false);
  const [savedContentStatus, setSavedContentStatus] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [rehearseLoading, setRehearseLoading] = useState(false);
  const [insightIndex, setInsightIndex] = useState(0);
  const OPERATIONAL_INSIGHTS = useMemo(() => [
    "Being exposed to this pattern again and again wears down your sense of stability over time.",
    "This pattern often escalates when you push back directly.",
    "The pressure compounds through repetition and not knowing what's coming next.",
    "People who deal with this often notice their self-trust takes a hit over time.",
    "How often the pattern repeats matters more than how intense any single message feels.",
  ], []);
  useEffect(() => {
    const t = setInterval(() => setInsightIndex((i) => (i + 1) % OPERATIONAL_INSIGHTS.length), 7000);
    return () => clearInterval(t);
  }, [OPERATIONAL_INSIGHTS.length]);

  useEffect(() => {
    if (!passedAnalysisId) return;
    (async () => {
      const { data } = await (await import("@/integrations/supabase/client")).supabase
        .from("neutralizations")
        .select("raw_text, grey_rock_response, subtext_decoder, signals_detected, created_at, viral_script, content_status, manipulation_score, toxicity_score, manipulation_primary_type, manipulation_secondary_type")
        .eq("id", passedAnalysisId)
        .single();
      if (data?.raw_text && !passedMessage) setLoadedMessage(data.raw_text);
      if (data?.grey_rock_response) setSavedResponse(data.grey_rock_response);
      if (data?.subtext_decoder) setSavedDecoder(data.subtext_decoder);
      if (data?.signals_detected) setSavedSignals(data.signals_detected);
      if (data?.created_at) setSavedTimestamp(data.created_at);
      if (data?.viral_script) setSavedViralScript(true);
      if (data?.content_status) setSavedContentStatus(data.content_status);
      if (data && typeof data.manipulation_score === "number") {
        // Saved reports show the stored engine values, never a browser re-score.
        setSavedEngine({
          manipulation_score: data.manipulation_score,
          toxicity_score: data.toxicity_score,
          manipulation_primary_type: data.manipulation_primary_type,
          secondary_pattern: data.manipulation_secondary_type,
          subtext_decoder: data.subtext_decoder,
        });
      }
    })();
  }, [passedAnalysisId, passedMessage]);
  const [savedEngine, setSavedEngine] = useState<any>(null);

  const isConversation = analysisMode === "conversation";
  const recoveredMessage = !passedMessage ? localStorage.getItem("greyrock_pending_analysis") : null;
  if (recoveredMessage) localStorage.removeItem("greyrock_pending_analysis");
  const displayMessage = passedMessage || loadedMessage || recoveredMessage || DEFAULT_MESSAGE;

  useEffect(() => {
    if (displayMessage && displayMessage !== DEFAULT_MESSAGE) {
      sessionStorage.setItem("greyrock_last_analysis_msg", displayMessage);
      if (passedAnalysisId) {
        sessionStorage.setItem("greyrock_last_analysis_id", passedAnalysisId);
      }
    }
  }, [displayMessage, passedAnalysisId]);

  const engineResult = (location.state as any)?.engineResult ?? savedEngine;
  const isQuickScan = !!(location.state as any)?.quickScan;
  const analysis = useMemo(() => {
    const base = routeAnalysis(displayMessage);
    if (!engineResult) return base;
    // Engine payload wins: headline score, pattern and signals come from the paid engine only.
    const mScore = typeof engineResult.manipulation_score === "number" ? engineResult.manipulation_score : base.manipulation_score;
    const tScore = typeof engineResult.toxicity_score === "number" ? engineResult.toxicity_score : base.toxicity_score;
    return {
      ...base,
      // Headline = manipulation score, the same number Reports shows.
      score: mScore,
      verdict: mScore >= 7 ? "MANIPULATION DETECTED" : mScore >= 4 ? "AMBIGUOUS SIGNAL" : "LOW SIGNAL",
      manipulation_score: mScore,
      toxicity_score: tScore,
      confidence: engineResult.confidence_index || base.confidence,
      nuance: engineResult.nuance || engineResult.subtext_decoder || base.nuance,
      primary_pattern: engineResult.manipulation_primary_type || base.primary_pattern,
    };
  }, [displayMessage, engineResult]);
  const TARGET_SCORE = analysis.score;
  const MANIPULATION_LABEL = analysis.verdict;
  const radarDataFull = analysis.radarData;
  const manipulationScore = analysis.manipulation_score;
  const toxicityScore = analysis.toxicity_score;
  const confidence = analysis.confidence;
  const nuance = analysis.nuance;
  const primaryPattern = analysis.primary_pattern;
  const fallbackSignals = useMemo(
    () => (savedSignals && savedSignals.length > 0
      ? savedSignals
      : deriveFallbackSignals(primaryPattern, MANIPULATION_LABEL)),
    [savedSignals, primaryPattern, MANIPULATION_LABEL]
  );
  // Prefer pattern-specific copy over generic verdict-based copy
  const patternDisplayKey = (PATTERN_LABELS[primaryPattern] || primaryPattern?.replace(/_/g, " ") || "").toUpperCase();
  const tacticalInfo = TACTICAL_DESCRIPTIONS[patternDisplayKey] || TACTICAL_DESCRIPTIONS[MANIPULATION_LABEL] || TACTICAL_DESCRIPTIONS["MANIPULATION DETECTED"];
  const patternLabel = PATTERN_LABELS[primaryPattern] || primaryPattern?.replace(/_/g, " ") || "Unknown";

  // ── Secondary pattern detection from radar data ──
  const RADAR_AXIS_TO_PATTERN: Record<string, string> = {
    "Gaslighting": "Gaslighting",
    "Invalidation": "Invalidation",
    "Deflection": "Deflection",
    "Control": "Control Framing",
    "Blame Shift": "Blame Shifting",
  };
  const PATTERN_TO_RADAR_AXIS: Record<string, string> = {
    "Gaslighting": "Gaslighting",
    "Invalidation": "Invalidation",
    "Soft Invalidation": "Invalidation",
    "Deflection": "Deflection",
    "Control Framing": "Control",
    "Blame Shifting": "Blame Shift",
    "Guilt-Tripping": "Control",
    "Passive Aggression": "Control",
    "Triangulation": "Deflection",
    "Minimization": "Invalidation",
    "Victim Reversal": "Blame Shift",
    "Shame Attack": "Invalidation",
    "False Consensus": "Deflection",
    "Tone Policing": "Invalidation",
    "Emotional Blackmail": "Control",
  };
  const secondaryPatternInfo = useMemo(() => {
    // "Also detected" requires a headline of 4.0+.
    if (manipulationScore < 4) return null;
    if (engineResult) {
      // Paid results: only the engine's own secondary pattern, never the browser chart.
      const sec = engineResult.secondary_pattern;
      if (!sec || sec === "none" || sec === primaryPattern) return null;
      if (engineResult.confidence_index === "low") return null;
      const label = PATTERN_LABELS[sec] || sec.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
      if (label === patternLabel) return null;
      return { axis: label, label, value: 100 };
    }
    // Sort radar axes by score descending — this is the single source of truth
    const sorted = [...radarDataFull].sort((a, b) => b.value - a.value);
    // Find which radar axis the primary pattern maps to, so we skip it
    const primaryRadarAxis = PATTERN_TO_RADAR_AXIS[patternLabel] || null;
    // Pick the highest-scoring axis that isn't the primary's axis
    const secondary = sorted.find(d => d.axis !== primaryRadarAxis);
    if (!secondary || secondary.value < 40) return null;
    const secondaryLabel = RADAR_AXIS_TO_PATTERN[secondary.axis] || secondary.axis;
    return { axis: secondary.axis, label: secondaryLabel, value: secondary.value };
  }, [radarDataFull, patternLabel, manipulationScore, engineResult, primaryPattern]);
  const secondaryTacticalInfo = secondaryPatternInfo
    ? TACTICAL_DESCRIPTIONS[secondaryPatternInfo.label.toUpperCase()] || null
    : null;

  const escalationStages = useMemo(() => {
    if (!isConversation) return null;
    const patterns = ["Deflection", "Invalidation", "Gaslighting"];
    if (MANIPULATION_LABEL.includes("INVALIDATION")) {
      patterns[2] = "Invalidation";
      patterns[1] = "Minimization";
    } else if (MANIPULATION_LABEL.includes("MINIMIZATION")) {
      patterns[2] = "Minimization";
      patterns[1] = "Deflection";
    }
    return [
      { label: "Initiation", pattern: patterns[0] },
      { label: "Escalation", pattern: patterns[1] },
      { label: "Peak", pattern: patterns[2] },
    ];
  }, [isConversation, MANIPULATION_LABEL]);

  const [phase, setPhase] = useState<Phase>("scanning");
  const [displayScore, setDisplayScore] = useState(0);
  const [visibleFlags, setVisibleFlags] = useState(0);
  const [showLabel, setShowLabel] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [breathe, setBreathe] = useState(false);
  const [impactPulse, setImpactPulse] = useState(false);
  const [gridVisible, setGridVisible] = useState(false);
  const [strokeProgress, setStrokeProgress] = useState(0);
  const [fillOpacity, setFillOpacity] = useState(0);
  const [visibleDots, setVisibleDots] = useState(0);
  const [radarValues, setRadarValues] = useState(0);

  // ── Staged hero reveal states ──
  const [chipsRevealed, setChipsRevealed] = useState(false);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [showCTA, setShowCTA] = useState(false);

  // Severity-sensitive glow intensity
  const glowIntensity = TARGET_SCORE >= 7 ? 0.7 : TARGET_SCORE >= 4 ? 0.45 : 0.25;

  const viralCardRef = useRef<HTMLDivElement>(null);
  const explainerCardRef = useRef<HTMLDivElement>(null);
  const patternFirstCardRef = useRef<HTMLDivElement>(null);
  const neutralizerRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const animFrameRef = useRef<number>(0);
  const countDoneRef = useRef(false);

  // ── Phase sequencing ──
  useEffect(() => {
    const t = setTimeout(() => setPhase("counting"), 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== "counting") return;
    const duration = 650;
    const startTime = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(parseFloat((eased * TARGET_SCORE).toFixed(1)));
      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(tick);
      } else {
        countDoneRef.current = true;
        setPhase("impact");
      }
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [phase]);

  useEffect(() => {
    if (phase !== "impact") return;
    setImpactPulse(true);
    const t1 = setTimeout(() => {
      setImpactPulse(false);
      setTimeout(() => setPhase("label"), 120);
    }, 220);
    return () => clearTimeout(t1);
  }, [phase]);

  useEffect(() => {
    if (phase !== "label") return;
    setShowLabel(true);
    const t = setTimeout(() => setPhase("grid"), 200);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "grid") return;
    setShowContent(true);
    setGridVisible(true);
    const t = setTimeout(() => setPhase("draw"), 300);
    return () => clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (phase !== "draw") return;
    const duration = 600;
    const startTime = performance.now();
    let fillTriggered = false;
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 2);
      setStrokeProgress(eased);
      setRadarValues(eased);
      if (progress >= 0.75 && !fillTriggered) {
        fillTriggered = true;
        const fillStart = performance.now();
        const fillTick = (n: number) => {
          const fp = Math.min((n - fillStart) / 200, 1);
          setFillOpacity(fp * 0.35);
          if (fp < 1) requestAnimationFrame(fillTick);
        };
        requestAnimationFrame(fillTick);
      }
      if (progress < 1) requestAnimationFrame(tick);
      else setPhase("dots");
    };
    requestAnimationFrame(tick);
  }, [phase]);

  useEffect(() => {
    if (phase !== "dots") return;
    const total = radarDataFull.length;
    let count = 0;
    const interval = setInterval(() => {
      count++;
      setVisibleDots(count);
      if (count >= total) {
        clearInterval(interval);
        setPhase("complete");
      }
    }, 60);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    if (!showLabel) return;
    const totalFlags = analysis.flagCount;
    if (visibleFlags >= totalFlags) return;
    const interval = setInterval(() => {
      setVisibleFlags((prev) => {
        if (prev >= totalFlags) { clearInterval(interval); return prev; }
        return prev + 1;
      });
    }, 100);
    return () => clearInterval(interval);
  }, [showLabel, visibleFlags]);

  // ── Staged hero reveal: chips → interpretation → CTA ──
  useEffect(() => {
    if (!showLabel) return;
    const t1 = setTimeout(() => setChipsRevealed(true), 180);
    const t2 = setTimeout(() => setShowInterpretation(true), 480);
    const t3 = setTimeout(() => setShowCTA(true), 700);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [showLabel]);

  useEffect(() => {
    if (phase === "complete") {
      const t = setTimeout(() => setBreathe(true), 200);
      return () => clearTimeout(t);
    }
  }, [phase]);

  // Public feed is offline: no feed submission from the result page.

  const downloadCard = async (
    cardRef: React.RefObject<HTMLDivElement | null>,
    width: number,
    height: number,
    filename: string
  ) => {
    if (!cardRef.current) return;
    setIsGenerating(true);
    
    try {
      await document.fonts.ready;
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#0a0a0a",
        scale: 2,
        width,
        height,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast({ title: "Downloading...", description: filename });
    } catch {
      toast({ title: "Download failed", description: "Could not generate share card.", variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  

  const radarData = radarDataFull.map((d) => ({ ...d, value: d.value * radarValues }));

  const scoreColor =
    TARGET_SCORE >= 7
      ? "hsl(var(--drama-high))"
      : TARGET_SCORE >= 4
      ? "hsl(var(--drama-mid))"
      : "hsl(210 15% 65%)";

  const ringColor =
    TARGET_SCORE >= 7
      ? "var(--drama-high)"
      : TARGET_SCORE >= 4
      ? "var(--drama-mid)"
      : "210 15% 65%";

  const POLYGON_PERIMETER = 800;


  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start"
      style={{ background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)" }}
    >
      {user && <IntelligenceTopBar />}

      <main className="w-full max-w-2xl flex flex-col items-center gap-3 px-4 pt-5 pb-8 md:py-10">
        <div className="w-full flex flex-col items-center text-center mb-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">Your Analysis</h1>
          <p className="text-xs md:text-sm text-muted-foreground/70 font-light mt-1 lowercase tracking-wide">what just happened</p>
        </div>
        {/* ── Breadcrumb ── */}
        <PageBreadcrumb items={[
          { label: fromReports ? "Reports" : "Analyze", path: fromReports ? "/reports" : (user ? "/dashboard" : "/") },
          { label: fromReports ? "Saved Report" : "Analysis" }
        ]} />

        {/* ── Saved Report Context Banner ── */}
        {fromReports && passedAnalysisId && (
          <div className="w-full rounded-lg border border-cyan/15 bg-cyan/5 px-4 py-2.5 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <BookmarkCheck className="w-3.5 h-3.5 text-cyan" />
              <span className="text-[11px] font-mono text-cyan uppercase tracking-wide">Saved Report</span>
            </div>
            {savedTimestamp && (
              <span className="text-[11px] text-muted-foreground font-mono">
                {new Date(savedTimestamp).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                {" · "}
                {new Date(savedTimestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </span>
            )}
            <div className="flex items-center gap-1.5 ml-auto flex-wrap">
              {savedResponse && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-mono">
                  <MessageSquareText className="w-2.5 h-2.5" /> Response
                </span>
              )}
              {savedViralScript && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 text-[10px] font-mono">
                  <Film className="w-2.5 h-2.5" /> Script
                </span>
              )}
              {savedContentStatus && savedContentStatus !== "backlog" && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[10px] font-mono">
                  <FileDown className="w-2.5 h-2.5" /> Exported
                </span>
              )}
              <button
                onClick={() => navigate("/reports")}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors ml-1"
              >
                <ArrowLeft className="w-3 h-3" /> Back to Reports
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── 1. HERO RESULT BLOCK (above the fold) ── */}
        {/* ═══════════════════════════════════════════════════════ */}

        {/* Analyzed Message — with forensic sweep during intake */}
        <div
          className={`w-full rounded-xl border bg-card/60 transition-all duration-500 relative overflow-hidden ${
            phase === "scanning" ? "opacity-80 border-cyan/30" : "opacity-100 border-border"
          }`}
          style={{ padding: "16px 20px", borderLeft: `3px solid ${phase === "scanning" ? "hsl(var(--cyan))" : scoreColor}` }}
        >
          {/* Forensic sweep overlay */}
          {phase === "scanning" && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(90deg, transparent 0%, hsl(var(--cyan) / ${glowIntensity * 0.15}) 45%, hsl(var(--cyan) / ${glowIntensity * 0.25}) 50%, hsl(var(--cyan) / ${glowIntensity * 0.15}) 55%, transparent 100%)`,
                animation: "forensicSweep 0.8s ease-in-out",
              }}
            />
          )}
          <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5 font-mono">
            Analyzed Message
          </p>
          <p className="text-sm text-foreground/80 italic leading-relaxed">{displayMessage}</p>
        </div>

        {/* Score + Pattern + Confidence + Chips + Explanation */}
        <div className="flex flex-col items-center gap-2 min-h-[200px] justify-center">
          {phase === "scanning" && (
            <div className="flex flex-col items-center gap-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  background: "hsl(var(--cyan))",
                  boxShadow: `0 0 ${12 + glowIntensity * 20}px hsl(184 100% 50% / ${glowIntensity}), 0 0 ${24 + glowIntensity * 30}px hsl(184 100% 50% / ${glowIntensity * 0.5})`,
                  animation: "pulse 1.2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                }}
              />
              <p className="text-xs font-mono tracking-[0.2em] text-cyan/80 uppercase">
                Reading the conversation…
              </p>
            </div>
          )}

          {phase !== "scanning" && (
            <div className="relative flex flex-col items-center gap-1.5">
              {/* Score Ring */}
              <div className="relative w-32 h-32 md:w-40 md:h-40 flex items-center justify-center">
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 160 160">
                  <circle cx="80" cy="80" r="72" fill="none" stroke="hsl(0 0% 15%)" strokeWidth="6" />
                  <circle
                    cx="80" cy="80" r="72" fill="none"
                    stroke={scoreColor}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 72}`}
                    strokeDashoffset={`${2 * Math.PI * 72 * (1 - displayScore / 10)}`}
                    style={{
                      transition: "stroke-dashoffset 300ms ease-out",
                      filter: `drop-shadow(0 0 8px hsl(${ringColor} / 0.5))`,
                    }}
                  />
                </svg>
                <div
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{
                    background: `radial-gradient(circle, hsl(${ringColor} / ${impactPulse ? "0.4" : "0.15"}) 0%, transparent 70%)`,
                    animation: breathe ? "breatheScore 4s ease-in-out infinite" : undefined,
                  }}
                />
                <div className="relative flex flex-col items-center z-10">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-mono">
                    Pattern Strength
                  </span>
                  <span
                    className="text-5xl md:text-6xl font-bold tabular-nums font-mono"
                    style={{
                      color: scoreColor,
                      transform: impactPulse ? "scale(1.06)" : "scale(1)",
                      transition: "transform 220ms ease-out",
                    }}
                  >
                    {displayScore.toFixed(1)}
                  </span>
                  <span className="text-xs font-mono" style={{ color: "hsl(0 0% 55% / 0.58)" }}>/ 10</span>
                </div>
              </div>

              {/* Pattern + Confidence (single compact row) */}
              <div
                className="flex flex-col items-center gap-2 mt-1 transition-all duration-700 ease-out"
                style={{
                  opacity: showLabel ? 1 : 0,
                  transform: showLabel ? "translateY(0)" : "translateY(12px)",
                }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-mono text-xs font-bold uppercase tracking-wider border"
                    style={{
                      color: TARGET_SCORE >= 7 ? "hsl(var(--drama-high))" : TARGET_SCORE >= 4 ? "hsl(var(--drama-mid))" : "hsl(210 15% 65%)",
                      borderColor: TARGET_SCORE >= 7 ? "hsl(var(--drama-high) / 0.3)" : TARGET_SCORE >= 4 ? "hsl(var(--drama-mid) / 0.3)" : "hsl(210 15% 40% / 0.3)",
                      background: TARGET_SCORE >= 7 ? "hsl(var(--drama-high) / 0.08)" : TARGET_SCORE >= 4 ? "hsl(var(--drama-mid) / 0.08)" : "hsl(210 15% 40% / 0.06)",
                    }}
                  >
                    <Shield className="w-3 h-3" />
                    {patternLabel}
                  </span>
                  <span className="text-[10px] font-mono text-muted-foreground">•</span>
                  <span
                    className="text-[10px] uppercase tracking-[0.12em] font-mono font-semibold"
                    style={{
                      color: confidence === "high" ? "hsl(var(--drama-high))" : confidence === "moderate" ? "hsl(var(--drama-mid))" : "hsl(210 15% 65%)",
                    }}
                  >
                    {confidence.toUpperCase()} CONFIDENCE
                  </span>
                  {isQuickScan && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md font-mono text-[10px] font-semibold uppercase tracking-wider border border-border text-muted-foreground bg-secondary/60">
                      Quick scan
                    </span>
                  )}
                </div>
                {secondaryPatternInfo && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.12em] font-mono text-muted-foreground">Also Detected:</span>
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md font-mono text-[10px] font-semibold uppercase tracking-wider border"
                      style={{
                        color: "hsl(var(--drama-mid))",
                        borderColor: "hsl(var(--drama-mid) / 0.25)",
                        background: "hsl(var(--drama-mid) / 0.06)",
                      }}
                    >
                      {secondaryPatternInfo.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Signal Chips — staggered reveal */}
              <div
                className="transition-all duration-500 ease-out"
                style={{
                  opacity: chipsRevealed ? 1 : 0,
                  transform: chipsRevealed ? "translateY(0)" : "translateY(8px)",
                }}
              >
                <SignalChips signals={fallbackSignals} staggerDelay={chipsRevealed ? 120 : 0} />
              </div>

              {/* One-line forensic explanation — resolves last */}
              {(savedDecoder || tacticalInfo.shortExplanation) && (
                <p
                  className="text-[13px] text-foreground/60 italic font-mono text-center max-w-md leading-relaxed mt-1 transition-all duration-500 ease-out"
                  style={{
                    opacity: showInterpretation ? 1 : 0,
                    transform: showInterpretation ? "translateY(0)" : "translateY(6px)",
                  }}
                >
                  "{savedDecoder || tacticalInfo.shortExplanation}"
                </p>
              )}
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── HERO ACTION ROW ── */}
        {/* ═══════════════════════════════════════════════════════ */}
        {showContent && (
          <div className="w-full flex flex-col sm:flex-row gap-2.5 transition-all duration-500 ease-out" style={{
            opacity: showCTA ? 1 : 0,
            transform: showCTA ? "translateY(0)" : "translateY(12px)",
          }}>
            {/* Primary CTA — scrolls to strategy panel */}
            <Button
              onClick={() => neutralizerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="flex-1 h-12 text-sm font-bold bg-gradient-to-r from-cyan to-cyan/80 text-primary-foreground hover:opacity-90 glow-cyan rounded-xl gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Write a Reply — 1 credit
              <ArrowRight className="w-3.5 h-3.5 ml-0.5" />
            </Button>
            {/* Secondary CTAs */}
            <div className="flex gap-2.5">
              <Button
                onClick={() => downloadCard(patternFirstCardRef, 1080, 1920, "GreyRockAI-Analysis.png")}
                disabled={isGenerating}
                variant="ghost"
                className="flex-1 sm:flex-none h-12 px-5 flex items-center justify-center gap-2 bg-muted/40 border border-border hover:border-cyan/40 hover:bg-cyan/5 text-muted-foreground hover:text-cyan rounded-xl transition-all"
              >
                <Share2 className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wider">Share</span>
              </Button>
              {fromReports && passedAnalysisId ? (
                <ForensicReportExport
                  message={displayMessage}
                  patternLabel={patternLabel}
                  manipulationScore={manipulationScore}
                  toxicityScore={toxicityScore}
                  confidence={confidence}
                  signals={fallbackSignals}
                  shortExplanation={tacticalInfo.shortExplanation}
                  nuance={nuance}
                  savedResponse={savedResponse}
                  savedTimestamp={savedTimestamp}
                  hasScript={savedViralScript}
                  hasExported={savedContentStatus ? savedContentStatus !== "backlog" : false}
                  primaryPattern={primaryPattern}
                />
              ) : (
                <Button
                  onClick={() => {
                    if (user) {
                      navigate("/vault");
                    } else {
                      localStorage.setItem("greyrock_pending_analysis", displayMessage);
                      setShowUnlockModal(true);
                    }
                  }}
                  variant="ghost"
                  className="flex-1 sm:flex-none h-12 px-5 flex items-center justify-center gap-2 bg-muted/40 border border-border hover:border-amber-500/40 hover:bg-amber-500/5 text-muted-foreground hover:text-amber-400 rounded-xl transition-all"
                >
                  <FileDown className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    {contextMode === "professional" ? "HR Memo" : "Save"}
                  </span>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ── Conversation Pattern Timeline ── */}
        {isConversation && escalationStages && showLabel && (
          <div
            className="w-full flex flex-col items-center transition-all duration-500"
            style={{
              opacity: showContent ? 1 : 0,
              transform: showContent ? "translateY(0)" : "translateY(10px)",
            }}
          >
            <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-mono mb-3">
              How it unfolded
            </p>
            <div className="flex items-center gap-0 w-full max-w-md">
              {escalationStages.map((stage, i) => (
                <div key={stage.label} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className="w-3 h-3 rounded-full border-2 mb-1.5"
                      style={{
                        borderColor: i === 2 ? "hsl(var(--drama-high))" : "hsl(var(--cyan))",
                        background: i === 2 ? "hsl(var(--drama-high))" : i === 1 ? "hsl(var(--cyan))" : "transparent",
                      }}
                    />
                    <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
                      {stage.label}
                    </span>
                    <span
                      className="text-[11px] font-mono font-semibold mt-0.5"
                      style={{ color: i === 2 ? "hsl(var(--drama-high))" : "hsl(var(--cyan))" }}
                    >
                      {stage.pattern}
                    </span>
                  </div>
                  {i < 2 && (
                    <div className="h-px w-full bg-border relative -top-3">
                      <div className="absolute top-1/2 right-0 -translate-y-1/2 text-muted-foreground text-[8px]">→</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── 2. UNDERLYING TACTIC ── */}
        {/* ═══════════════════════════════════════════════════════ */}
        {showContent && (
          <div
            className="w-full transition-all duration-700"
            style={{
              opacity: showContent ? 1 : 0,
              transform: showContent ? "translateY(0)" : "translateY(20px)",
            }}
          >
            <div className="w-full rounded-xl border border-amber-500/20 bg-amber-500/5 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-4 h-4 text-amber-400" />
                <h3 className="text-xs uppercase font-mono font-semibold tracking-[0.14em] text-amber-400">
                  What's underneath
                </h3>
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed border-l-2 border-amber-500/30 pl-4">
                {tacticalInfo.description}
              </p>
              {isConversation && (
                <p className="text-xs text-cyan/70 font-mono mt-3 leading-relaxed pl-4">
                  ⚡ This pattern was detected as an escalation across multiple exchanges.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── 3. COMPACT DUAL METRICS ── */}
        {/* ═══════════════════════════════════════════════════════ */}
        {showContent && (
          <div
            className="w-full grid grid-cols-2 gap-3 transition-all duration-700"
            style={{
              opacity: showContent ? 1 : 0,
              transform: showContent ? "translateY(0)" : "translateY(20px)",
            }}
          >
            {/* Manipulation Likelihood — Compact */}
            <div className="rounded-xl border border-border bg-card/60 px-4 py-3 flex items-center gap-3">
              <span
                className="text-2xl font-bold font-mono tabular-nums shrink-0"
                style={{ color: manipulationScore >= 7 ? "hsl(var(--drama-high))" : manipulationScore >= 4 ? "hsl(var(--drama-mid))" : "hsl(var(--cyan))" }}
              >
                {manipulationScore.toFixed(1)}
              </span>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-mono leading-tight">
                  Manipulation
                </span>
                <span
                  className="text-[10px] font-mono font-semibold uppercase"
                  style={{ color: manipulationScore >= 7 ? "hsl(var(--drama-high))" : manipulationScore >= 4 ? "hsl(var(--drama-mid))" : "hsl(var(--cyan))" }}
                >
                  {manipulationScore >= 7 ? "High" : manipulationScore >= 4 ? "Mixed" : "Low"}
                </span>
              </div>
            </div>

            {/* Emotional Harm — Compact */}
            <div className="rounded-xl border border-border bg-card/60 px-4 py-3 flex items-center gap-3">
              <span
                className="text-2xl font-bold font-mono tabular-nums shrink-0"
                style={{ color: toxicityScore >= 7 ? "hsl(var(--drama-high))" : toxicityScore >= 4 ? "hsl(var(--drama-mid))" : "hsl(var(--cyan))" }}
              >
                {toxicityScore.toFixed(1)}
              </span>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-mono leading-tight">
                  Emotional Harm
                </span>
                <span
                  className="text-[10px] font-mono font-semibold uppercase"
                  style={{ color: toxicityScore >= 7 ? "hsl(var(--drama-high))" : toxicityScore >= 4 ? "hsl(var(--drama-mid))" : "hsl(var(--cyan))" }}
                >
                  {toxicityScore >= 7 ? "High" : toxicityScore >= 4 ? "Moderate" : "Low"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── 4. NUANCE ASSESSMENT ── */}
        {/* ═══════════════════════════════════════════════════════ */}
        {showContent && nuance && (
          <div
            className="w-full transition-all duration-700"
            style={{
              opacity: showContent ? 1 : 0,
              transform: showContent ? "translateY(0)" : "translateY(20px)",
            }}
          >
            <div className="w-full rounded-xl border border-border bg-card/60 p-3.5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                <h3 className="text-[11px] uppercase font-mono font-semibold tracking-[0.14em] text-muted-foreground">
                  The fuller picture
                </h3>
              </div>
              <p className="text-[13px] text-foreground/70 leading-snug border-l-2 border-muted-foreground/20 pl-3">
                {nuance}
                {secondaryPatternInfo && secondaryTacticalInfo && (
                  <span className="block mt-1.5 text-foreground/60">
                    A secondary {secondaryPatternInfo.label.toLowerCase()} element is also present — {secondaryTacticalInfo.shortExplanation.toLowerCase()}
                  </span>
                )}
              </p>
              <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-mono">Primary:</span>
                  <span className="text-[10px] font-mono font-semibold text-foreground/80 uppercase">{patternLabel}</span>
                </div>
                {secondaryPatternInfo && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-mono">Secondary:</span>
                    <span className="text-[10px] font-mono font-semibold uppercase" style={{ color: "hsl(var(--drama-mid))" }}>
                      {secondaryPatternInfo.label}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-mono">Confidence:</span>
                  <span
                    className="text-[10px] font-mono font-semibold uppercase inline-block"
                    style={{
                      color: confidence === "high" ? "hsl(var(--drama-high))" : confidence === "moderate" ? "hsl(var(--drama-mid))" : "hsl(210 15% 65%)",
                      animation: "confidenceStabilize 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
                    }}
                  >
                    {confidence}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── 5. ADVANCED ANALYSIS (collapsible radar) ── */}
        {/* ═══════════════════════════════════════════════════════ */}
        {showContent && (
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="w-full">
            <CollapsibleTrigger asChild>
              <button
                className="w-full flex flex-col items-center gap-1 py-3 rounded-xl border border-border bg-card/40 hover:bg-card/60 text-muted-foreground hover:text-foreground transition-all duration-200"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase font-mono font-semibold tracking-[0.14em]">
                    {advancedOpen ? "Hide detail" : "See more detail"}
                  </span>
                  <ChevronDown
                    className="w-4 h-4 transition-transform duration-200"
                    style={{ transform: advancedOpen ? "rotate(180deg)" : "rotate(0)" }}
                  />
                </div>
                {!advancedOpen && (
                  <span className="text-[10px] text-muted-foreground/50 font-mono">
                    Pattern map, confidence, and the deeper detail
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div
                className="mt-3 glass-card p-6"
                style={{
                  boxShadow: `0 16px 48px -12px hsl(0 0% 0% / 0.9), 0 0 24px -8px hsl(184 100% 50% / 0.05)`,
                  animation: breathe ? "breatheRadar 4s ease-in-out infinite" : undefined,
                }}
              >
                <p className="text-xs uppercase text-muted-foreground font-mono mb-4 text-center" style={{ letterSpacing: "0.076em" }}>
                  Pattern map
                </p>
                <div className="w-full h-[300px] md:h-[340px]" style={{ marginTop: "-25px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                      <PolarGrid
                        stroke="hsl(0 0% 20%)"
                        style={{
                          opacity: gridVisible ? 1 : 0,
                          transform: gridVisible ? "scale(1)" : "scale(0.85)",
                          transformOrigin: "center",
                          transition: "opacity 300ms ease-out, transform 300ms ease-out",
                        }}
                      />
                      <PolarAngleAxis
                        dataKey="axis"
                        tick={{
                          fill: "hsl(0 0% 95% / 0.9)",
                          fontSize: 13.2,
                          fontFamily: "JetBrains Mono, monospace",
                        }}
                        style={{
                          opacity: gridVisible ? 1 : 0,
                          transition: "opacity 300ms ease-out",
                        }}
                      />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar
                        name="Score"
                        dataKey="value"
                        stroke="hsl(184 100% 50%)"
                        fill="hsl(184 100% 50%)"
                        fillOpacity={fillOpacity}
                        strokeWidth={2}
                        strokeDasharray={POLYGON_PERIMETER}
                        strokeDashoffset={POLYGON_PERIMETER * (1 - strokeProgress)}
                        dot={(props: any) => (
                          <VertexDot key={props.index} cx={props.cx} cy={props.cy} visible={props.index < visibleDots} />
                        )}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-1.5 mt-2">
                  <div className="flex justify-between text-[11px] font-mono text-muted-foreground uppercase tracking-wider">
                    <span>Confidence</span>
                    <span style={{ color: scoreColor }}>{TARGET_SCORE}/10</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: `${(TARGET_SCORE / 10) * 100}%`,
                        background: `linear-gradient(90deg, hsl(var(--drama-mid)), ${scoreColor})`,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* ── Creator Tools ── */}
              <div className="mt-6 rounded-xl border border-border bg-card/30 p-5">
                <p className="text-xs uppercase text-muted-foreground font-mono mb-4 text-center" style={{ letterSpacing: "0.076em" }}>
                  Creator Export Tools
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => downloadCard(viralCardRef, 1080, 1920, "GreyRockAI-Creator.png")}
                    disabled={isGenerating}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-card/40 hover:bg-muted/60 hover:border-cyan/30 transition-all text-left group"
                  >
                    <Film className="w-4 h-4 text-muted-foreground group-hover:text-cyan transition-colors shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-foreground/90">Creator Card</div>
                      <div className="text-[11px] text-muted-foreground font-mono">9:16 · Stories & Reels</div>
                    </div>
                  </button>
                  <button
                    onClick={() => downloadCard(explainerCardRef, 1080, 1350, "GreyRockAI-Explainer.png")}
                    disabled={isGenerating}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border bg-card/40 hover:bg-muted/60 hover:border-cyan/30 transition-all text-left group"
                  >
                    <Eye className="w-4 h-4 text-muted-foreground group-hover:text-cyan transition-colors shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-foreground/90">Explainer Card</div>
                      <div className="text-[11px] text-muted-foreground font-mono">4:5 · Posts & Carousels</div>
                    </div>
                  </button>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── 6. RESPONSE STRATEGY ── */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div
          ref={neutralizerRef}
          className="w-full transition-all duration-700"
          style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? "translateY(0)" : "translateY(20px)",
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Response Strategy
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {user ? (
            <NeutralizerChat
              message={displayMessage}
              score={TARGET_SCORE}
              verdict={MANIPULATION_LABEL}
              savedResponse={savedResponse}
              savedDecoder={savedDecoder}
              analysisId={passedAnalysisId}
              contextMode={contextMode}
              relationshipType={relationshipType}
              tactic={primaryPattern}
            />
          ) : (
            <div className="w-full rounded-xl border border-cyan/20 bg-card overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-cyan" />
                <h3 className="text-[11px] uppercase font-mono font-semibold tracking-[0.16em] text-foreground">
                  Suggested Grey Rock Reply
                </h3>
              </div>

              {/* Blurred Partial Reveal */}
              <div className="px-4 pt-3 pb-0 relative">
                <p className="text-[13px] text-foreground/85 leading-snug">
                  "I hear what you are saying, but I need to
                  <span className="inline-block" style={{
                    filter: "blur(5px)",
                    opacity: 0.5,
                    userSelect: "none",
                  }}>
                    {" "}be clear about my own experience here. What you're describing doesn't align with what I remember, and I'm choosing to trust my own recollection. I don't need to convince you — I just need you to understand my boundary.
                  </span>
                </p>
                <div className="h-6 bg-gradient-to-b from-transparent to-card" />
              </div>

              {/* Mode segmented preview */}
              <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
                <span className="text-[9.5px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono shrink-0">
                  Mode
                </span>
                <div className="inline-flex p-0.5 rounded-lg bg-background/60 border border-border/80">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-emerald-500/10 text-emerald-400 shadow-[inset_0_0_0_1px_currentColor] font-semibold">
                    <ShieldCheck className="w-2.5 h-2.5" /> Soft Neutral
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider text-muted-foreground/55">
                    <RefreshCw className="w-2.5 h-2.5" /> Firm Boundary
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider text-muted-foreground/55">
                    <Ban className="w-2.5 h-2.5" /> Minimal Grey Rock
                  </span>
                </div>
              </div>

              {/* Unlock CTA */}
              <div className="px-4 pb-4 flex flex-col items-center gap-2">
                <Button
                  onClick={() => {
                    localStorage.setItem("greyrock_pending_analysis", displayMessage);
                    setShowUnlockModal(true);
                  }}
                  className="h-10 w-full max-w-sm text-[13px] font-bold bg-gradient-to-r from-cyan to-cyan/80 text-primary-foreground hover:opacity-90 hover:shadow-[0_0_28px_-4px_hsl(184_100%_50%/0.6)] glow-cyan rounded-lg transition-shadow"
                >
                  <Lock className="mr-2 h-3.5 w-3.5" />
                  Unlock Full Response — 1 Credit
                </Button>
                <p className="text-[10px] text-muted-foreground/55 font-mono">
                  Strategy tested in 18,421+ similar behavioral patterns.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* ── 7. NEXT ACTION ── */}
        {/* ═══════════════════════════════════════════════════════ */}
        {showContent && (
          <div className="w-full mt-3">
            <div className="flex items-center gap-3 mb-2.5">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-cyan/80 font-mono font-semibold">
                What you can do next
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-2.5">
              {/* CARD 1 — REHEARSE (dominant) */}
              <button
                disabled={!analysis || TARGET_SCORE === 0 || rehearseLoading}
                onClick={() => {
                  const patterns = radarDataFull
                    .map((d) => ({ name: d.axis, score: d.value }))
                    .filter((p) => p.score > 0)
                    .sort((a, b) => b.score - a.score);
                  navigate("/rehearsal", {
                    state: { entry_type: "single_message", patterns: patterns.slice(0, 3) },
                  });
                }}
                className="md:col-span-3 group relative overflow-hidden text-left rounded-xl border border-cyan/30 bg-gradient-to-br from-cyan/[0.08] via-card/80 to-card/60 px-4 py-3 hover:border-cyan/70 hover:from-cyan/[0.16] hover:shadow-[0_0_48px_-10px_hsl(184_100%_50%/0.55),inset_0_1px_0_hsl(184_100%_50%/0.18)] transition-all duration-300 disabled:opacity-50"
                style={{ boxShadow: "0 0 32px -12px hsl(184 100% 50% / 0.25), inset 0 1px 0 hsl(184 100% 50% / 0.1)" }}
              >
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan/50 to-transparent group-hover:via-cyan transition-colors" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 50%, hsl(184 100% 50% / 0.08), transparent 60%)" }} />
                <div className="flex items-center gap-2 mb-1.5">
                  <Crosshair className="w-3.5 h-3.5 text-cyan" />
                  <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-cyan/80 font-semibold">
                    Practice first
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-foreground mb-0.5 tracking-tight leading-tight">
                  Practice what to say
                </h3>
                <p className="text-[12px] text-muted-foreground/80 leading-snug mb-2.5">
                  Try the conversation in a safe space before you have it for real.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan text-primary-foreground text-[11px] font-bold uppercase tracking-wider group-hover:opacity-90 transition-opacity">
                    Start practicing
                    <ArrowRight className="w-3 h-3" />
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground/55 uppercase tracking-wider">Uses 2 credits</span>
                </div>
              </button>

              {/* CARD 2 — BUILD RESPONSE STRATEGY */}
              <button
                onClick={() => {
                  if (user) {
                    neutralizerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  } else {
                    localStorage.setItem("greyrock_pending_analysis", displayMessage);
                    setShowUnlockModal(true);
                  }
                }}
                className="md:col-span-2 group text-left rounded-xl border border-border bg-card/60 px-4 py-3 hover:border-emerald-500/40 hover:bg-card/80 transition-all duration-300"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[10px] uppercase tracking-[0.16em] font-mono text-emerald-400/80 font-semibold">
                    Response Strategy
                  </span>
                </div>
                <h3 className="text-sm font-bold text-foreground mb-0.5 tracking-tight leading-tight">
                  Get your reply
                </h3>
                <p className="text-[11px] text-muted-foreground/80 leading-snug mb-2">
                  Calibrated replies — soft, firm, or grey rock.
                </p>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-foreground/70">
                    <span className="w-1 h-1 rounded-full bg-emerald-400" />Soft
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-foreground/70">
                    <span className="w-1 h-1 rounded-full bg-cyan" />Firm
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-foreground/70">
                    <span className="w-1 h-1 rounded-full bg-amber-400" />Grey Rock
                  </span>
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-400 group-hover:gap-2.5 transition-all">
                  Build
                  <ArrowRight className="w-3 h-3" />
                </div>
              </button>

              {/* CARD 3 — EXPLORE SIMILAR SIGNALS (compact strip) */}
              <div className="md:col-span-5 rounded-xl border border-border bg-card/40 px-3.5 py-2.5 hover:border-amber-500/30 transition-all duration-300">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 shrink-0">
                    <Radio className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-[10px] uppercase tracking-[0.16em] font-mono text-amber-400/80 font-semibold">
                      Similar Signals
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
                    {getSimilarPhrases(primaryPattern).map((chip) => (
                      <button
                        key={chip}
                        onClick={() => navigate("/analysis-result", { state: { message: chip, contextMode } })}
                        className="px-2 py-0.5 rounded-md bg-background/50 border border-border/50 hover:border-amber-500/50 hover:bg-amber-500/[0.05] hover:shadow-[0_0_10px_-2px_hsl(43_96%_56%/0.4)] transition-all max-w-full"
                      >
                        <span className="text-[10.5px] text-foreground/70 font-mono leading-none truncate inline-block max-w-[220px] align-middle">
                          "{chip}"
                        </span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate(user ? "/trends" : "/")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-amber-500/30 bg-amber-500/5 text-[10px] font-bold uppercase tracking-wider text-amber-400 hover:bg-amber-500/10 transition-all whitespace-nowrap shrink-0"
                  >
                    Pattern Map
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════ */}
            {/* ── 8. EXPORT DOSSIER (compact toolbar) ── */}
            {/* ═══════════════════════════════════════════════════════ */}
            <div className="w-full mt-4">
              <div className="flex items-center gap-3 mb-2.5">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono font-semibold">
                  Save & share
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  {
                    icon: FileText,
                    label: "PDF Brief",
                    ext: "PDF",
                    onClick: () => exportPdfBrief({
                      message: displayMessage,
                      patternLabel,
                      manipulationScore,
                      toxicityScore,
                      confidence,
                      signals: fallbackSignals,
                      shortExplanation: savedDecoder || tacticalInfo.shortExplanation,
                      nuance,
                      savedResponse,
                    }),
                    disabled: isGenerating,
                  },
                  {
                    icon: Target,
                    label: "Summary",
                    ext: "TXT",
                    onClick: () => exportTextFile(
                      buildTacticalSummary({
                        message: displayMessage,
                        patternLabel,
                        score: TARGET_SCORE,
                        manipulationScore,
                        toxicityScore,
                        confidence,
                        signals: fallbackSignals,
                        shortExplanation: savedDecoder || tacticalInfo.shortExplanation,
                        nuance,
                        savedResponse,
                      }),
                      "GreyRockAI-Tactical-Summary.txt",
                    ),
                  },
                  {
                    icon: Share2,
                    label: "Share Card",
                    ext: "PNG",
                    onClick: () => downloadCard(patternFirstCardRef, 1080, 1920, "GreyRockAI-Share-Card.png"),
                    disabled: isGenerating,
                  },
                  {
                    icon: Camera,
                    label: "Conversation",
                    ext: "TXT",
                    onClick: () => exportTextFile(
                      buildConversationSnapshot({
                        message: displayMessage,
                        patternLabel,
                        score: TARGET_SCORE,
                        timestamp: savedTimestamp,
                      }),
                      "GreyRockAI-Conversation-Snapshot.txt",
                    ),
                  },
                ].map(({ icon: Icon, label, ext, onClick, disabled }) => (
                  <button
                    key={label}
                    onClick={onClick}
                    disabled={disabled}
                    className="group relative flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card/50 hover:border-cyan/40 hover:bg-cyan/[0.04] hover:shadow-[0_0_16px_-4px_hsl(184_100%_50%/0.45)] transition-all duration-200 text-left disabled:opacity-50"
                  >
                    <Icon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-cyan transition-colors shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-foreground/90 truncate leading-tight">
                        {label}
                      </div>
                      <div className="text-[8.5px] font-mono tracking-[0.2em] text-muted-foreground/50 group-hover:text-cyan/70 transition-colors leading-tight mt-0.5">
                        .{ext}
                      </div>
                    </div>
                    <span className="absolute top-1.5 right-1.5 w-1 h-1 rounded-full bg-muted-foreground/20 group-hover:bg-cyan group-hover:shadow-[0_0_6px_hsl(184_100%_50%)] transition-all" />
                  </button>
                ))}
              </div>
            </div>

            {/* Operational footer */}
            <div className="w-full flex items-center justify-between flex-wrap gap-3 px-1 pt-3 mt-3 border-t border-border/40">
              <div className="flex items-center gap-2.5 flex-wrap">
                <button
                  onClick={() => navigate(user ? "/dashboard?focus=analyze" : "/")}
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground/60 hover:text-cyan transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  New Analysis
                </button>
                <span className="text-[10px] text-muted-foreground/30">·</span>
                <span className="text-[10px] text-muted-foreground/40 font-mono uppercase tracking-wider">Accurate?</span>
                <button
                  onClick={() => toast({ title: "Logged", description: "Thanks — noted." })}
                  className="text-[11px] px-2 py-0.5 rounded border border-border/60 text-muted-foreground/60 hover:border-emerald-500/40 hover:text-emerald-400 transition-all font-mono"
                >
                  +
                </button>
                <button
                  onClick={() => toast({ title: "Logged", description: "Thanks — we'll take a look." })}
                  className="text-[11px] px-2 py-0.5 rounded border border-border/60 text-muted-foreground/60 hover:border-red-500/40 hover:text-red-400 transition-all font-mono"
                >
                  −
                </button>
              </div>

            </div>

            {/* Rotating intelligence insight */}
            <div className="w-full mt-3 px-1 flex items-center gap-2 min-h-[18px]">
              <span className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.22em] text-muted-foreground/40 shrink-0">
                <span
                  className="w-1.5 h-1.5 rounded-full bg-cyan/60"
                  style={{ animation: "intelPulse 2.4s ease-in-out infinite" }}
                />
                Worth knowing
              </span>
              <p
                key={insightIndex}
                className="text-[10.5px] font-mono text-muted-foreground/55 leading-snug tracking-wide italic truncate"
                style={{ animation: "intelFade 600ms ease-out" }}
              >
                {OPERATIONAL_INSIGHTS[insightIndex]}
              </p>
            </div>
            <style>{`
              @keyframes intelPulse {
                0%, 100% { opacity: 0.45; box-shadow: 0 0 0 0 hsl(184 100% 50% / 0); }
                50% { opacity: 1; box-shadow: 0 0 8px hsl(184 100% 50% / 0.55); }
              }
              @keyframes intelFade {
                0% { opacity: 0; transform: translateY(2px); }
                100% { opacity: 0.55; transform: translateY(0); }
              }
              @keyframes signalPulse {
                0%, 100% { box-shadow: 0 0 0 0 hsl(184 100% 50% / 0); }
                50% { box-shadow: 0 0 12px hsl(184 100% 50% / 0.35); }
              }
              @keyframes confidenceStabilize {
                0% { opacity: 0; transform: scale(0.85); filter: blur(2px); }
                60% { opacity: 1; transform: scale(1.04); filter: blur(0); }
                100% { opacity: 1; transform: scale(1); filter: blur(0); }
              }
            `}</style>
          </div>
        )}


      </main>

      <UnlockStrategyModal open={showUnlockModal} onOpenChange={setShowUnlockModal} />
      <ViralPosterCard
        ref={viralCardRef}
        message={displayMessage}
        score={TARGET_SCORE}
        pattern={patternLabel}
        interpretation={savedDecoder || tacticalInfo.description}
        confidence={confidence}
      />
      <EducationalExplainerCard
        ref={explainerCardRef}
        message={displayMessage}
        pattern={patternLabel}
        secondaryPattern={secondaryPatternInfo?.label}
        signals={fallbackSignals}
        interpretation={savedDecoder || tacticalInfo.description}
        manipulationScore={manipulationScore}
        emotionalHarmScore={toxicityScore}
      />
      <PatternFirstCard
        ref={patternFirstCardRef}
        message={displayMessage}
        pattern={patternLabel}
        secondaryPattern={secondaryPatternInfo?.label}
        interpretation={savedDecoder || tacticalInfo.description}
        threatScore={manipulationScore}
        emotionalHarmScore={toxicityScore}
        signals={fallbackSignals}
      />

      <style>{`
        @keyframes breatheScore {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.06); }
        }
        @keyframes breatheRadar {
          0%, 100% { box-shadow: 0 16px 48px -12px hsl(0 0% 0% / 0.9), 0 0 24px -8px hsl(184 100% 50% / 0.05); }
          50% { box-shadow: 0 16px 48px -12px hsl(0 0% 0% / 0.9), 0 0 30px -4px hsl(184 100% 50% / 0.10); }
        }
      `}</style>
    </div>
  );
}
